<template>
  <div :class="['insights-container', module]">
    <!-- Barra de insight que aparece quando o painel não está visível -->
    <div class="insights-bar" @click="toggleInsightsPanel" v-if="!showInsightsPanel">
      <img src="/ins.png" alt="Logo" style="width: 180px; margin-right: 10px" />
      <img
        src="/lamp-icon.png"
        alt="Ícone"
        style="width: 15px; height: 15px; margin-left: 10px; margin-top: 10px"
      />
      <span style="font-size: 14px; margin-top: 10px; margin-left: 5px; margin-right: 10px">
        {{ insightsCount }} {{ insightsCount > 1 ? 'recomendações' : 'recomendação' }}
      </span>
    </div>

    <!-- Administrador - Configuração (somente admin) -->
    <InsightsAdmin 
      v-if="isAdmin"
      :module="module"
      :roleIdProp="roleIdProp"
      :prompts="prompts"
      :tabsContent="tabsContent"
      :roles="roles"
      :promptsTypes="promptsTypes"
      :promptsTypesModalsDays="promptsTypesModalsDays"
      :newPrompt="newPrompt"
      :states="states"
      :loadPromptDebate="loadPromptDebate"
      @create-prompt="createPrompt"
      @update-prompt="updatePrompt"
      @delete-prompt="deletePrompt"
      @validate-prompt="validatePrompt"
      @update-personas="updatePersonas"
      @get-role-id="getRoleId"
      @get-prediction-days="getPredictionDays"
      @handle-city-update="handleCityUpdate"
      @handle-state-update="handleStateUpdate"
      @version-insight="versionInsight"
      @error="showToastError"
    />

    <!-- Painel lateral com os insights -->
    <Sidebar
      :visible.sync="showInsightsPanel"
      :baseZIndex="1050"
      :dismissable="false"
      position="right"
      class="w-full md:w-80rem lg:w-90rem insights-sidebar"
    >
      <div class="insights-sidebar-content">
        <img src="/i-SET.png" alt="Logo" style="width: 180px" />
        <h2 class="insights-title">Insights</h2>

        <!-- Conteúdo dos insights -->
        <div class="insights-content-container">
          <!-- TreeView por Cidade -->
          <InsightsTreeView
            v-if="viewMode === 'tree'"
            :module="module"
            :insights="insightsFormatted"
            :states="states"
            :getPromptTypeLabel="getPromptTypeLabel"
            @open-insights="openInsights"
          />

          <!-- Listagem simples de insights -->
          <InsightsList 
            v-else
            :module="module"
            :insights="insightsFormatted"
            :getPromptTypeLabel="getPromptTypeLabel"
            @open-insights="openInsights"
          />
        </div>
      </div>
    </Sidebar>
    
    <!-- Conteúdo dos insights abertos -->
    <InsightsContent 
      :module="module"
      :showOpenAiResponse="showOpenAiResponse"
      :isLoadingInsights="isLoadingInsights"
      :isUploadingInsights="isUploadingInsights"
      :insightsToShow="insightsToShow"
      :extractTitle="extractTitle"
      :parseEnumeratedText="parseEnumeratedText"
      @update-insights="updateInsights"
      @close="closeOpenAiResponseContainer"
    />
    
  </div>
</template>

<script>
import axios from 'axios'
import Multiselect from 'vue-multiselect'
import Accordion from 'primevue/accordion'
import Sidebar from 'primevue/sidebar'
import AccordionTab from 'primevue/accordiontab'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import Skeleton from 'primevue/skeleton'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import Toast from 'primevue/toast'
import SelectInput from '@/Shared/SelectInput'
import InsightsAdmin from './InsightsAdmin.vue'
import InsightsList from './InsightsList.vue'
import InsightsTreeView from './InsightsTreeView.vue'
import InsightsContent from './InsightsContent.vue'
import { useStateCityStore } from '@/Stores/stateCityStore'
import { useInsightStore } from '@/stores/insightStore'
import { mapState } from 'pinia'

export default {
  name: 'Insights',
  components: {
    Accordion,
    AccordionTab,
    Multiselect,
    Sidebar,
    TabView,
    TabPanel,
    Skeleton,
    Button,
    ProgressSpinner,
    Toast,
    SelectInput,
    InsightsAdmin,
    InsightsList,
    InsightsTreeView,
    InsightsContent
  },
  props: {
    module: {
      type: String,
      required: true
    },
    showInstant: {
      type: Boolean,
      default: true
    },
    // Props para dados e configurações
    roleIdProp: {
      type: Number,
      default: null
    },
    citiesString: {
      type: String,
      default: ''
    },
    states: {
      type: Array,
      default() {
        return []
      }
    },
    data: {
      type: Object,
      default() {
        return {}
      }
    },
    // Opções de fonte de dados específicas por módulo
    dataSourceOptions: {
      type: Array,
      default() {
        return []
      }
    },
    // API customizações
    insightsApiUrl: {
      type: String,
      default: '/api/insights'
    },
    promptsApiUrl: {
      type: String,
      default: '/api/prompts'
    }
  },
  data() {
    const store = useStateCityStore()
    return {
      store,
      viewMode: 'list', // 'tree' ou 'list'
      prompts: [],
      insights: [],
      insightsToShow: [],
      promptValidationData: [],
      roles: [],
      selectedCity: null,
      selectedState: null,
      pollingInterval: null,
      pollingIntervalTime: 30000, // 10 segundos entre verificações
      showInsightsPanel: false,
      showPromptValidation: false,
      showOpenAiResponse: false,
      isLoadingInsights: false,
      isUploadingInsights: false,
      tabsContent: this.prepareVariabelsInPromt(),
      newPrompt: {
        title: '',
        text: '',
        type: null,
        role_id: null,
        state: null,
        city: null
      },
      promptsTypes: [
        { label: 'Geral', value: 'general' },
        { label: 'Performance Econômica', value: 'economic_performance' },
        { label: 'Perfil', value: 'profile' },
        { label: 'Avaliações', value: 'reviews' },
        { label: 'Mapa Interativo', value: 'interactive_map' },
        { label: 'Rodoviário', value: 'road' },
        { label: 'Aéreo', value: 'air' },
        { label: 'Marítimo', value: 'maritime' },
      ],
      promptsTypesModalsDays: [
        { label: '7 dias', value: 7 },
        { label: '15 dias', value: 15 },
        { label: '30 dias', value: 30 },
        { label: '60 dias', value: 60 },
        { label: '90 dias', value: 90 },
      ],
      debateMetrics: null,
      selectedPersonas: []
    }
  },
  computed: {
    isAdmin() {
      return this.$page.props.auth.user.email === 'super@smart-destination.com' || 
             this.$page.props.auth.user.admin === true;
    },
    insightsCount() {
      // Mostrar apenas a contagem de insights formatados (já filtrados por status completed)
      return this.insightsFormatted.length || 0
    },
    insightsFormatted() {
      // Usar o método transformPromptsToInsights para filtrar apenas os prompts com status "completed"
      return this.transformPromptsToInsights(this.prompts || [])
    },
    computedSelectedCity() {
      return this.selectedCity || this.newPrompt.city
    },
    computedSelectedState() {
      return this.selectedState || this.newPrompt.state
    },
    ...mapState(useInsightStore, ['insightData'])

  },
  mounted() {
    this.getPrompts()
    this.getRoles()
    
    // Iniciar verificação de prompts pendentes após carregar os dados
    this.$nextTick(() => {
      this.startPollingIfNeeded()
    })
  },
  
  // Limpar o intervalo quando o componente é destruído
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    // ====== Métodos principais ======
    toggleInsightsPanel() {
      this.showInsightsPanel = !this.showInsightsPanel
    },
    async openInsights(insightGroup) {
      this.showOpenAiResponse = true
      this.isLoadingInsights = true
      const debateId = insightGroup.debate_id
      try {
        const response = await axios.get(`/api/user-insight/${debateId}`)
        if (response) {
          this.insightsToShow = response
        }
      } catch (error) {
        console.error('Erro ao carregar insights:', error)
        this.showToastError(error)
      } finally {
        this.isLoadingInsights = false
      }
    },
    closeOpenAiResponseContainer() {
      this.showOpenAiResponse = false
      this.insightsToShow = []
    },
    
    // ====== Métodos de formatação ======
    parseHeader(key) {
      return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
    },
    extractTitle(text) {
      if (!text) return ''
      try {
        const match = text.match(/^\s*#\s*(.+?)(?:\n|$)/)
        return match ? match[1].trim() : ''
      } catch (e) {
        return ''
      }
    },
    parseEnumeratedText(text) {
      if (!text) return []
      try {
        return text
          .replace(/^#\s*.+\n/, '')
          .split(/\n\d+[\.\)]\s+/)
          .filter(Boolean)
          .map(item => item.trim())
      } catch (e) {
        return [text]
      }
    },
    
    // ====== Métodos de API ======
    async getPrompts() {
      try {
        console.log('Buscando prompts para o módulo:', this.module);
        const response = await axios.get(this.promptsApiUrl, {
          params: {
            module: this.module, // Usar 'module' em vez de 'type' para corresponder ao backend
            // role_id: this.roleIdProp // Comentado temporariamente para ignorar o filtro de role_id
          }
        });
        this.prompts = response.data;
        console.log('Prompts recebidos:', this.prompts.length);
        // Verificar se há prompts pendentes e iniciar polling se necessário
        this.startPollingIfNeeded();
      } catch (error) {
        console.error('Erro ao buscar prompts:', error);
        this.showToastError(error);
      }
    },
    
    // Iniciar polling se houver prompts pendentes
    startPollingIfNeeded() {
      // Verificar se há prompts pendentes (status diferente de 'completed')
      const pendingPrompts = this.prompts.filter(p => p.status !== 'completed');
      
      if (pendingPrompts.length > 0) {
        
        // Parar qualquer polling existente antes de iniciar um novo
        this.stopPolling();
        
        // Iniciar um novo intervalo de verificação
        this.pollingInterval = setInterval(() => {
          this.checkPendingPromptsStatus();
        }, this.pollingIntervalTime);
      } else {
        // Se não há prompts pendentes, garantir que o polling está parado
        this.stopPolling();
      }
    },
    
    // Verificar o status dos prompts pendentes
    async checkPendingPromptsStatus() {
      // Obter IDs dos prompts pendentes
      const pendingPromptIds = this.prompts
        .filter(p => p.status !== 'completed')
        .map(p => p.id);
      
      if (pendingPromptIds.length === 0) {
        // Se não há mais prompts pendentes, parar o polling
        this.stopPolling();
        return;
      }
      
      try {
        // Fazer requisição para verificar o status dos prompts pendentes
        // Usar a rota correta que recebe um array de IDs
        const response = await axios.post('/api/user-prompts/check-status', {
          promptIds: pendingPromptIds
        });
        
        // Verificar se algum prompt mudou de status
        if (response.data.updatedPrompts && response.data.updatedPrompts.length > 0) {
          await this.getPrompts();
        }
      } catch (error) {
        console.error('Erro ao verificar status dos prompts:', error);
        // Não mostrar toast para não incomodar o usuário com erros de polling
      }
    },
    
    // Parar o polling
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async updateInsights(insights) {
      this.isUploadingInsights = true
      try {
        await axios.post(`${this.insightsApiUrl}/update`, insights)
        this.getPrompts()
        this.showToastSuccess('Insights atualizados com sucesso')
      } catch (error) {
        this.showToastError(error)
      } finally {
        this.isUploadingInsights = false
      }
    },
    
    // ====== Métodos de administração ======
    async createPrompt() {
      
      try {
        // Validar cidade e estado apenas para insights de social_media
        if (this.module === 'social_media' && (!this.store.getState || !this.store.getCity)) {
          this.showToastError('Cidade e Estado são obrigatórios para insights de redes sociais');
          return;
        }

        // Preparar dados do prompt usando dados do insightData
        const data = {
          title: this.insightData.title,
          text: this.insightData.text,
          module: this.module,
          role_id: this.insightData.role_id,
          state: this.insightData.moduleFields[this.module].state.toString(),
          state_name: this.insightData.moduleFields[this.module].state_name,
          city: this.insightData.moduleFields[this.module].city,
          enabled: true,
          module_id: this.insightData.module_id,
          rounds: this.insightData.rounds,
          use_reasoning: this.insightData.use_reasoning,
          agents: this.insightData.agents,
          selectedPersonas: this.insightData.selectedPersonas,
          data_sources: JSON.stringify(this.insightData.selectedDataSources || []), // Convertendo para JSON string
          status: 'processing' // Definindo status inicial como processando
        };

        // Adicionar campos específicos por módulo
        if (this.module === 'social_media') {
          data.state = String(this.store.getState || '');
          data.state_name = this.store.getStateName;
          data.city = this.store.getCity;
          data.search_term = this.store.searchTerm.toString();
          console.log('Termo de pesquisa enviado para o backend:', data.search_term);
        } else if (this.module === 'reviews') {
          // Sem campos específicos para o módulo reviews por enquanto
        }

        // Criar o prompt
        const promptResponse = await axios.post(this.promptsApiUrl, data);

        // Preparar dados para o debate
        const debateData = {
          topic: data.title,
          context: data.text,
          max_turns: data.rounds || 3,
          personas: data.selectedPersonas || [],
          use_reasoning: data.use_reasoning,
          state: String(data.state || ''),
          state_name: data.state_name,
          city: data.city,
          search_term: data.search_term,
          agents: data.agents,
          data_sources: data.data_sources,
          module: data.module,
          citiesString: this.citiesString,
        };
        // Criar o debate
        const debateResponse = await axios.post('/api/set-ia-consensus/debates', debateData);

        // Atualizar o debate_id no prompt
        await axios.put(`${this.promptsApiUrl}/${promptResponse.data.id}/debate`, {
          debate_id: debateResponse.data.process_id,
          status: 'processing'
        });

        // Atualizar a lista de prompts imediatamente
        await this.getPrompts();

        this.resetNewPrompt();
        this.showToastSuccess('Prompt criado com sucesso');

        // Iniciar polling específico para este prompt
        this.startPollingIfNeeded();
      } catch (error) {
        console.error('Erro completo:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar prompt';
        this.showToastError(errorMessage);
      }
    },
    
    async updatePrompt(prompt) {
      try {
        console.log('Atualizando prompt:', prompt);
        
        // Preparar os dados para atualização
        const data = {
          title: prompt.title,
          text: prompt.text,
          module: prompt.module,
          role_id: prompt.role_id,
          enabled: prompt.enabled !== undefined ? prompt.enabled : true,
          module_id: prompt.module_id,
          rounds: prompt.rounds || 1,
          use_reasoning: prompt.use_reasoning !== undefined ? prompt.use_reasoning : true,
          data_sources: JSON.stringify(prompt.selectedDataSources || []), // Convertendo para JSON string
        };
        
        // Adicionar campos específicos por módulo
        const module = prompt.module;
        if (module === 'social_media') {
          data.state = prompt.state || (prompt.moduleFields && prompt.moduleFields.social_media ? prompt.moduleFields.social_media.state : '');
          data.state_name = prompt.state_name || (prompt.moduleFields && prompt.moduleFields.social_media ? prompt.moduleFields.social_media.state_name : '');
          data.city = prompt.city || (prompt.moduleFields && prompt.moduleFields.social_media ? prompt.moduleFields.social_media.city : '');
          data.search_term = prompt.search_term || (prompt.moduleFields && prompt.moduleFields.social_media ? prompt.moduleFields.social_media.search_term : '');
        } else if (module === 'reviews') {
          // Sem campos específicos para o módulo reviews por enquanto
        }
        
        // Usar a API para atualizar o prompt
        await axios.put(`${this.promptsApiUrl}/${prompt.id}`, data);
        
        // Atualizar a lista de prompts
        await this.getPrompts();
        this.showToastSuccess('Prompt atualizado com sucesso');
      } catch (error) {
        console.error('Erro ao atualizar prompt:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar prompt';
        this.showToastError(errorMessage);
      }
    },
    async deletePrompt(promptId) {
      // Verificar se recebemos um ID ou um objeto prompt completo
      const id = typeof promptId === 'object' ? promptId.id : promptId;
      
      try {
        // Usar a nova API de prompts
        await axios.delete(`${this.promptsApiUrl}/${id}`)
        
        // Remover o prompt da lista local
        this.prompts = this.prompts.filter(p => p.id !== id);
        this.insights = this.insights.filter(i => i.id !== id);
        
        // Atualizar a lista completa de prompts do backend
        await this.getPrompts()
        
        this.showToastSuccess('Insight excluído com sucesso')
      } catch (error) {
        console.error('Erro ao excluir insight:', error);
        this.showToastError(error)
      }
    },
    async validatePrompt(prompt) {
      this.showPromptValidation = true
      try {
        const variablesPrepared = this.prepareVariabelsInPromt()
        const parsedPrompts = this.parseVariabelsInPromts(prompt, variablesPrepared)
        const { data } = await axios.post(`${this.promptsApiUrl}/validate`, parsedPrompts)
        this.promptValidationData = data
        this.showToastSuccess('Prompt validado com sucesso')
      } catch (error) {
        this.showToastError(error)
      }
    },
    
    // ====== Métodos utilitários ======
    prepareVariabelsInPromt() {
      return {}
    },
    parseVariabelsInPromts(prompt, variables) {
      return { ...prompt }
    },
    preparePromptData(prompt) {
      let state = this.computedSelectedState;
      let city = this.computedSelectedCity;

      if (this.citiesString) {
        const parts = this.citiesString.split(' - ');
        if (parts.length === 2) {
          city = parts[0].trim();
          state = parts[1].trim();
        }
      }

      // Registrar aviso, mas não bloquear a criação do prompt
      if (!state || !city) {
        console.warn('Estado ou cidade não encontrados:', { state, city });
        // Usar valores padrão ou vazios em vez de lançar erro
        state = state || '';
        city = city || '';
      }

      const data = {
        title: prompt.title,
        text: prompt.text,
        type: prompt.type,
        roles_id: prompt.role_id,
        state: state,
        state_name: state,
        city: city,
        enabled: prompt.enabled
      };
      return data;
    },
    transformPromptsToInsights(prompts) {
      // Filtrar apenas os prompts com status "completed" (concluído)
      return prompts.filter(prompt => prompt.status === 'completed')
    },
    getPromptTypeLabel(type) {
      const promptType = this.promptsTypes.find(pt => pt.value === type)
      return promptType ? promptType.label : type
    },
    isPromptTypeModals(type) {
      return ['road', 'air', 'maritime'].includes(type)
    },
    resetNewPrompt() {
      this.newPrompt = {
        title: '',
        text: '',
        type: null,
        role_id: this.roleIdProp,
        state: this.computedSelectedState,
        city: this.computedSelectedCity
      }
    },
    
    // Handlers para seleção
    getRoleId(id) {
      // Implementar conforme necessário
    },
    async getRoles() {
      try {
        const response = await axios.get('/api/roles')
        this.roles = response.data
      } catch (error) {
        console.error('Error fetching roles:', error)
      }
    },
    getPredictionDays(days) {
      // Implementar conforme necessário
    },
    handleCityUpdate(item, newCityName) {
      this.store.setCity(newCityName)
      if (item) {
        item.city = newCityName
      }
      this.newPrompt.city = newCityName
    },
    handleStateUpdate(item, newState) {
      this.store.setState(newState)
      if (item) {
        item.state = newState
      }
      this.newPrompt.state = newState
    },
    
    // Notificações
    showToastSuccess(summary) {
      if (this.useToast && this.$toast) {
        this.$toast.add({
          severity: 'success',
          summary,
          life: 3000
        })
      }
    },
    showToastError(error) {
      if (this.useToast && this.$toast) {
        console.error('Erro sendo exibido:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: typeof error === 'string' ? error : (error.response?.data?.message || error.message || 'Ocorreu um erro'),
          life: 5000
        })
      }
    },
    async pollDebateStatus(prompt) {
      if (!prompt.debate_id) return;
      
      try {
        const response = await axios.get(`/api/user-prompts/${prompt.id}/status`);
        const { debate_status } = response.data;
        
        // Atualizar o status do debate no prompt
        const index = this.prompts.findIndex(p => p.id === prompt.id);
        if (index !== -1) {
          this.$set(this.prompts[index], 'debate_status', debate_status);
          
          // Se o debate estiver concluído, buscar os resultados
          if (debate_status.status === 'concluído') {
            const results = await this.getDebateResults(prompt.id);
            if (results) {
              this.$set(this.prompts[index], 'debate_status', {
                ...debate_status,
                messages: results.messages
              });
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status do debate:', error);
      }
    },
    
    async getDebateResults(promptId) {
      try {
        const response = await axios.get(`/api/user-prompts/${promptId}/results`);
        return response.data;
      } catch (error) {
        console.error('Erro ao buscar resultados do debate:', error);
        return null;
      }
    },
    
    startPolling() {
      this.stopPolling(); // Garantir que não haja polling duplicado
      
      // Iniciar polling para prompts em processamento
      this.pollingInterval = setInterval(async () => {
        const processingPrompts = this.prompts.filter(
          p => p.status === 'processing' || p.status === 'pending'
        );
        
        if (processingPrompts.length === 0) {
          this.stopPolling();
          return;
        }
        
        for (const prompt of processingPrompts) {
          await this.pollDebateStatus(prompt);
        }
      }, 5000); // Verificar a cada 5 segundos
    },
    
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async loadPromptDebate(prompt) {
      if (!prompt.debate_id) return;
      
      try {
        // Buscar detalhes completos do debate usando o novo endpoint
        const response = await axios.get(`/api/debate-details/${prompt.debate_id}`);
        const { timeline, personas, model_statistics, total_interactions, debate_duration } = response.data;
        
        // Atualizar as informações do debate
        const debateInfo = {
          messages: timeline.map(msg => ({
            id: msg.id,
            content: msg.content,
            persona_id: msg.persona_id,
            created_at: msg.created_at,
            prompt_input: msg.prompt_input,
            prompt_output: msg.prompt_output
          })),
          personas: personas.map(p => ({
            id: p.id,
            name: p.name,
            role: p.role,
            expertise: p.expertise
          })),
          modelStats: model_statistics,
          metrics: {
            total_interactions,
            debate_duration,
            model_statistics
          }
        };

        // Atualizar o estado
        this.debateMessages = debateInfo.messages;
        this.selectedPersonas = debateInfo.personas;
        this.debateMetrics = debateInfo.metrics;
        
        return debateInfo;
      } catch (error) {
        console.error('Erro ao carregar debate:', error);
        this.showToastError(error);
        return null;
      }
    },
    
    async updatePersonas(prompt, selectedPersonas) {
      try {
        const response = await axios.put(`/api/user-prompts/${prompt.id}/personas`, {
          selectedPersonas
        });
        
        this.showToastSuccess('Personas atualizadas com sucesso');
        
        // Atualizar o prompt com o novo debate_id
        const updatedPrompt = {
          ...prompt,
          debate_id: response.data.debate_id,
          status: 'processing'
        };
        
        // Atualizar o prompt na lista
        const index = this.prompts.findIndex(p => p.id === prompt.id);
        if (index !== -1) {
          this.$set(this.prompts, index, updatedPrompt);
        }
        
        // Iniciar polling específico para este prompt
        this.startPolling();
        
      } catch (error) {
        this.showToastError(error);
      }
    },
    async versionInsight(versionData) {
      try {
        
        // Guardar o ID do insight original para remoção da lista local
        const originalId = versionData.original_id;
        
        // Extrair estado e cidade dos objetos aninhados e adicioná-los ao nível raiz
        // Isso é necessário para que o backend salve esses valores corretamente
        if (versionData.module_fields && versionData.module_fields.state) {
          versionData.state = versionData.module_fields.state;
        } else if (versionData.moduleFields && versionData.moduleFields[versionData.module] && versionData.moduleFields[versionData.module].state) {
          versionData.state = versionData.moduleFields[versionData.module].state;
        }

        // Extrair o nome do estado
        if (versionData.module_fields && versionData.module_fields.state_name) {
          versionData.state_name = versionData.module_fields.state_name;
        } else if (versionData.moduleFields && versionData.moduleFields[versionData.module] && versionData.moduleFields[versionData.module].state_name) {
          versionData.state_name = versionData.moduleFields[versionData.module].state_name;
        }

        if (versionData.module_fields && versionData.module_fields.city) {
          versionData.city = versionData.module_fields.city;
        } else if (versionData.moduleFields && versionData.moduleFields[versionData.module] && versionData.moduleFields[versionData.module].city) {
          versionData.city = versionData.moduleFields[versionData.module].city;
        }
        
        // Enviar requisição para criar nova versão
        const response = await axios.post(`${this.promptsApiUrl}/version`, versionData);
        const newPrompt = response.data;
        
        // Preparar dados para o debate
        const debateData = {
          topic: versionData.title,
          context: versionData.text,
          max_turns: versionData.rounds || 3,
          personas: versionData.selectedPersonas || [],
          use_reasoning: versionData.use_reasoning || true,
          state: String(versionData.state || ''),
          state_name: versionData.state_name,
          city: versionData.city,
          search_term: versionData.search_term,
          agents: versionData.agents || []
        };

        // Criar o debate para a nova versão
        const debateResponse = await axios.post('/api/set-ia-consensus/debates', debateData);

        // Atualizar o debate_id no prompt
        await axios.put(`${this.promptsApiUrl}/${newPrompt.id}/debate`, {
          debate_id: debateResponse.data.process_id,
          status: 'processing'
        });

        // Remover o prompt original da lista local (mesmo que o backend já o filtre)
        if (originalId) {
          // Remover do array de prompts
          this.prompts = this.prompts.filter(p => p.id !== originalId);
          
          // Remover do array de insights (usado no painel lateral)
          this.insights = this.insights.filter(i => i.id !== originalId);
          
        }
        
        // Adicionar o novo prompt à lista
        this.prompts.unshift(newPrompt);
        
        // Atualizar a lista completa de prompts do backend
        await this.getPrompts();
        
        this.showToastSuccess('Nova versão do insight criada com sucesso');
        
        // Iniciar polling específico para este prompt
        this.startPollingIfNeeded();
      } catch (error) {
        console.error('Erro ao criar nova versão do insight:', error);
        if (error.response?.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors)
            .flat()
            .join(', ');
          this.showToastError(errorMessages);
        } else {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar nova versão do insight';
          this.showToastError(errorMessage);
        }
      }
    }
  },
  created() {
    // Iniciar polling quando o componente for criado
    this.startPolling();
  },
  beforeDestroy() {
    // Garantir que o polling seja interrompido quando o componente for destruído
    this.stopPolling();
  }
}
</script>

<style>
.insights-container {
  position: relative;
}

.insights-bar {
  position: fixed;
  bottom: 20px;
  right: 30px;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background-color: white;
  border-radius: 50px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
}

.insights-bar:hover {
  transform: translateY(-2px);
}

.insights-sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px 30px;
}

.insights-title {
  font-size: 24px;
  font-weight: 600;
  color: #1c3d5a;
  margin: 28px 0 32px 0;
}

.insights-content-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.view-toggle {
  display: flex;
  margin-bottom: 28px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  background-color: #f5f7fa;
}

.view-toggle-btn {
  flex: 1;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #637381;
}

.view-toggle-btn.active {
  background-color: #1c3d5a;
  color: white;
  font-weight: 600;
}

.view-toggle-btn:not(.active):hover {
  background-color: #edf1f7;
}

.view-toggle-btn i {
  font-size: 16px;
}

/* Garantir que o modal de conteúdo fique acima */
::v-deep .p-sidebar {
  z-index: 1050 !important;
}

::v-deep .p-sidebar-right {
  width: 60rem !important;
  max-width: 95% !important;
}

::v-deep .p-sidebar-content {
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.insights-sidebar {
  height: 100vh;
  width: 30rem;
  
}

@media (min-width: 768px) {
  ::v-deep .p-sidebar-right {
    width: 70rem !important;
  }
}

@media (min-width: 1200px) {
  ::v-deep .p-sidebar-right {
    width: 80rem !important;
  }
}
</style>